/**
 * Jeu de features TanStack du `DataTable` (plan v3, phases 4 et 6).
 *
 * Extrait de `data-table.tsx` en phase 6 : la `FilterBar` a besoin du même type
 * de table pour lire les facettes, et l'importer depuis `data-table.tsx` créerait
 * un cycle (la table rend la barre, la barre type la table).
 *
 * TanStack Table v9 : une state ou une méthode absente = une feature non
 * enregistrée ici, jamais un problème de typage.
 */
import {
  columnFacetingFeature,
  columnFilteringFeature,
  columnVisibilityFeature,
  createFacetedRowModel,
  createFacetedUniqueValues,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  rowPaginationFeature,
  rowSortingFeature,
  tableFeatures,
  type ColumnDef,
  type FilterFn,
  type RowData,
  type SortFn,
  type Table,
} from "@tanstack/react-table";
import { correspondRecherche, normaliserTexte } from "@repo/utils";

/**
 * Métadonnées de colonne propres au repo.
 *
 * Déclarées comme slot `columnMeta` des features plutôt que par augmentation de
 * module : la portée reste locale au `DataTable`, sans polluer le typage global
 * de toute app qui importerait TanStack.
 */
export interface ColonneMeta {
  /** Libellé du sélecteur « Colonnes ». Défaut : le `header` s'il est textuel. */
  libelle?: string;
  /** Largeur CSS posée sur le `<colgroup>` (ex. `"22%"`). */
  largeur?: string;
  /** Aligne la colonne à droite (actions, chiffres). */
  aDroite?: boolean;
  /** Centre la colonne (pastilles de score courtes). */
  auCentre?: boolean;
  /**
   * Colonne figée à gauche pendant le défilement horizontal.
   *
   * N'a de sens qu'en largeurs `auto` : c'est là que la table déborde, et
   * qu'une ligne de scores perd son candidat de vue.
   */
  figee?: boolean;
  /**
   * Colonne masquée tant que l'utilisateur ne la coche pas dans « Colonnes ».
   *
   * Sert aux colonnes qui existent surtout pour porter un filtre : elles
   * doivent vivre dans la table (une facette lit une colonne, pas des données
   * libres) sans encombrer une largeur d'écran pour autant.
   */
  masqueeParDefaut?: boolean;
}

export const FEATURES = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  columnVisibilityFeature,
  columnFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  columnFacetingFeature,
  facetedRowModel: createFacetedRowModel(),
  facetedUniqueValues: createFacetedUniqueValues(),
  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),
  columnMeta: {} as ColonneMeta,
});

/** Jeu de features du `DataTable`, pour typer les `ColumnDef` des consommateurs. */
export type DataTableFeatures = typeof FEATURES;

/** `ColumnDef` typée contre les features du `DataTable`. */
export type ColonneDataTable<T extends RowData> = ColumnDef<
  DataTableFeatures,
  T,
  unknown
>;

/** `sortFn` de colonne typée contre les features du `DataTable`. */
export type SortFnColonne<T extends RowData> = SortFn<DataTableFeatures, T>;

/** Table typée contre les features du `DataTable`. */
export type TableDataTable<T extends RowData> = Table<DataTableFeatures, T>;

/**
 * Filtre de facette : compare la valeur d'accès à une valeur ou à une liste.
 *
 * Le contrôle de la `FilterBar` étant multi-sélection dès qu'il y a plus de
 * deux valeurs, la valeur de filtre peut être un tableau. Une liste vide ne
 * filtre rien — c'est « toutes les valeurs », pas « aucune ligne ».
 */
export function filtreFacette<T extends RowData>(): FilterFn<
  DataTableFeatures,
  T
> {
  return (row, columnId, valeurFiltre) => {
    const attendues = Array.isArray(valeurFiltre)
      ? (valeurFiltre as unknown[])
      : [valeurFiltre];
    if (attendues.length === 0) return true;
    const valeur = row.getValue(columnId);
    return attendues.some((a) => a === valeur);
  };
}

/**
 * Filtre de recherche texte, accents et casse ignorés des deux côtés.
 *
 * `champs` lit la ligne d'origine : la recherche porte sur les données, pas sur
 * la valeur d'une seule colonne — taper un email trouve la ligne même quand la
 * colonne Email est masquée.
 */
export function filtreRecherche<T extends RowData>(
  champs: (ligne: T) => (string | undefined | null)[],
): FilterFn<DataTableFeatures, T> {
  const fn: FilterFn<DataTableFeatures, T> = (row, _columnId, valeurFiltre) =>
    correspondRecherche(champs(row.original as T), String(valeurFiltre ?? ""));
  // Sans ça, la chaîne vide serait « auto-retirée » de l'état des filtres par
  // l'heuristique par défaut — ce qui est justement le comportement voulu, mais
  // seulement pour une requête vide APRÈS normalisation (« ' ' » ne filtre rien).
  fn.autoRemove = (valeur) => normaliserTexte(String(valeur ?? "")) === "";
  return fn;
}

/** Tri par ordre métier explicite, les valeurs inconnues en dernier. */
export function triOrdonne<T extends RowData>(
  ordre: readonly string[],
): SortFn<DataTableFeatures, T> {
  const rang = (valeur: unknown) => {
    const index = ordre.indexOf(String(valeur));
    return index === -1 ? ordre.length : index;
  };
  // Comparateur ascendant uniquement : TanStack inverse lui-même en descendant.
  return (a, b, columnId) => rang(a.getValue(columnId)) - rang(b.getValue(columnId));
}

/**
 * Filtre de facette pour une colonne dont la ligne porte une LISTE de valeurs
 * (les templates d'une campagne, par exemple).
 *
 * `filtreFacette` compare la valeur d'accès, qui est ici une chaîne jointe
 * (`"pami, inbasket"`) : cocher « pami » ne trouverait rien. Ici la ligne passe
 * dès qu'elle contient au moins une des valeurs cochées (OU, pas ET) — « les
 * campagnes qui contiennent un PAMI », pas « celles qui n'ont que ça ».
 *
 * À coupler au `getUniqueValues` de la colonne, sans quoi les facettes
 * compteraient les combinaisons plutôt que les valeurs.
 */
export function filtreListe<T extends RowData>(
  champ: (ligne: T) => readonly string[],
): FilterFn<DataTableFeatures, T> {
  return (row, _columnId, valeurFiltre) => {
    const attendues = Array.isArray(valeurFiltre)
      ? (valeurFiltre as unknown[])
      : [valeurFiltre];
    if (attendues.length === 0) return true;
    const valeurs = champ(row.original);
    return attendues.some((a) => valeurs.includes(String(a)));
  };
}
